<template>
  <div class="chat-conversation-list">
    <!-- Search -->
    <div class="chat-conversation-list__search">
      <v-text-field
        v-model="search"
        placeholder="ค้นหาแชท..."
        prepend-inner-icon="mdi-magnify"
        dense
        outlined
        hide-details
        class="chat-search-input"
      />
    </div>

    <!-- Conversation Items -->
    <div class="chat-conversation-list__items">
      <div
        v-for="conv in filteredConversations"
        :key="conv.id"
        :class="[
          'chat-conversation-item',
          { 'chat-conversation-item--active': conv.id === activeConversationId },
        ]"
        @click="$emit('select', conv)"
      >
        <div class="chat-conversation-item__avatar">
          <v-avatar size="48" color="grey lighten-3">
            <img v-if="getAvatar(conv)" :src="getAvatar(conv)" :alt="getName(conv)" />
            <v-icon v-else color="grey" size="28">mdi-account</v-icon>
          </v-avatar>
          <span v-if="getOnlineStatus(conv)" class="online-dot"></span>
        </div>

        <div class="chat-conversation-item__body">
          <div class="chat-conversation-item__header">
            <span class="chat-conversation-item__name">{{ getName(conv) }}</span>
            <span class="chat-conversation-item__time">{{ formatTime(conv) }}</span>
          </div>
          <div class="chat-conversation-item__preview">
            <span class="chat-conversation-item__msg">{{ getPreview(conv) }}</span>
            <v-badge
              v-if="conv.unreadCount > 0"
              :content="conv.unreadCount > 99 ? '99+' : String(conv.unreadCount)"
              color="red accent-3"
              inline
              class="chat-conversation-item__badge"
            />
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="filteredConversations.length === 0" class="chat-conversation-list__empty">
        <v-icon size="48" color="grey lighten-1">mdi-chat-outline</v-icon>
        <p>ยังไม่มีแชท</p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ChatConversationList",
  props: {
    conversations: { type: Array, default: () => [] },
    activeConversationId: { type: String, default: "" },
    viewRole: { type: String, default: "seller" }, // "seller" shows user info, "user" shows seller info
  },
  data() {
    return {
      search: "",
    };
  },
  computed: {
    filteredConversations() {
      if (!this.search.trim()) return this.conversations;
      const q = this.search.trim().toLowerCase();
      return this.conversations.filter((c) => {
        const name = this.getName(c).toLowerCase();
        return name.includes(q);
      });
    },
  },
  methods: {
    getName(conv) {
      if (this.viewRole === "seller" || this.viewRole === "rider") {
        const u = conv.user;
        if (!u) return "ลูกค้า";
        return [u.firstName, u.lastName].filter(Boolean).join(" ") || "ลูกค้า";
      }
      // user view — could be seller or rider conversation
      if (conv.rider) {
        return [conv.rider.natId_firstName, conv.rider.natId_lastName].filter(Boolean).join(" ") || "ไรเดอร์";
      }
      return conv.seller?.shopName || "ร้านค้า";
    },
    getAvatar(conv) {
      let img = null;
      if (this.viewRole === "seller" || this.viewRole === "rider") {
        img = conv.user?.profileImage;
      } else if (conv.rider) {
        img = conv.rider.profileImage;
      } else {
        img = conv.seller?.storeImage;
      }
      if (!img) return null;
      return img.startsWith("http") ? img : `${this.$domain_api}${img}`;
    },
    getOnlineStatus(conv) {
      if (this.viewRole === "seller" || this.viewRole === "rider") return conv.user?.isOnline;
      if (conv.rider) return conv.rider.isOnline;
      return conv.seller?.isOnline;
    },
    getPreview(conv) {
      const msg = conv.lastMessage;
      if (!msg) return "เริ่มการสนทนา...";
      if (msg.content) return msg.content.length > 40 ? msg.content.substring(0, 40) + "…" : msg.content;
      return "📷 รูปภาพ";
    },
    formatTime(conv) {
      const msg = conv.lastMessage;
      if (!msg) return "";
      const d = new Date(msg.createdAt);
      const now = new Date();
      const isToday = d.toDateString() === now.toDateString();
      if (isToday) {
        return d.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit" });
      }
      const diff = Math.floor((now - d) / (1000 * 60 * 60 * 24));
      if (diff === 1) return "เมื่อวาน";
      return d.toLocaleDateString("th-TH", { day: "numeric", month: "short" });
    },
  },
};
</script>

<style scoped>
.chat-conversation-list {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
}

.chat-conversation-list__search {
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
}

.chat-search-input >>> .v-input__slot {
  background: #f5f5f5 !important;
  border-radius: 24px !important;
}

.chat-conversation-list__items {
  flex: 1;
  overflow-y: auto;
}

.chat-conversation-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.15s;
  border-bottom: 1px solid #f5f5f5;
}

.chat-conversation-item:hover {
  background: #f8f5ff;
}

.chat-conversation-item--active {
  background: #ede7f6 !important;
  border-left: 3px solid #667eea;
}

.chat-conversation-item__avatar {
  position: relative;
  flex-shrink: 0;
}

.online-dot {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 12px;
  height: 12px;
  background: #4caf50;
  border-radius: 50%;
  border: 2px solid #fff;
}

.chat-conversation-item__body {
  flex: 1;
  min-width: 0;
}

.chat-conversation-item__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.chat-conversation-item__name {
  font-weight: 600;
  font-size: 14px;
  color: #1a1a2e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chat-conversation-item__time {
  font-size: 11px;
  color: #999;
  flex-shrink: 0;
  margin-left: 8px;
}

.chat-conversation-item__preview {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.chat-conversation-item__msg {
  font-size: 13px;
  color: #777;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.chat-conversation-item__badge {
  margin-left: 8px;
  flex-shrink: 0;
}

.chat-conversation-list__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 16px;
  color: #999;
}

.chat-conversation-list__empty p {
  margin-top: 8px;
  font-size: 14px;
}
</style>
